import { useAuth } from '@clerk/clerk-react';
import globals from './globals';
import Logger from '@common/logger';
import routes from './routes';

const logger = Logger('clerkAuth');

// Store auth instance for non-React contexts
let authInstance: ReturnType<typeof useAuth> | null = null;

/**
 * Initialize auth instance for use in non-React contexts
 * This should be called from a React component that has access to useAuth
 * @param auth - The auth instance from useAuth hook
 */
export const initializeAuth = (auth: ReturnType<typeof useAuth>): void => {
  authInstance = auth;
};

/**
 * Get custom header for API requests using Clerk session token
 * Returns Bearer token format for API compatibility
 * @returns Promise<Record<string, string>> Authorization header object
 */
export const getCustomHeader = async (): Promise<Record<string, string>> => {
  try {
    // Use stored auth instance if available
    if (!authInstance) {
      logger.log('Auth instance not initialized, returning empty header');
      return {};
    }
    
    if (!authInstance.isSignedIn) {
      logger.log('User not signed in, returning empty header');
      return {};
    }

    // Get the session token from Clerk
    const token = await authInstance.getToken();
    
    if (!token) {
      logger.log('No token available, returning empty header');
      return {};
    }

    logger.log('Clerk session token obtained successfully');
    return {
      Authorization: `Bearer ${token}`
    };
    
  } catch (error) {
    logger.error('Error getting Clerk session token:', error);
    return {};
  }
};

/**
 * Clerk configuration object
 * Contains publishable key and routing configuration
 */
export const clerkConfig = {
  publishableKey: globals.CLERK_PUBLISHABLE_KEY,
  signInUrl: routes.auth.getSignInRoute(),
  signUpUrl: routes.auth.getSignUpRoute(), 
};

/**
 * Configure Clerk authentication
 * This replaces the configureAmplify function
 */
const configureClerk = (): void => {
  logger.log('Configuring Clerk authentication...');
  // Clerk configuration is handled by the ClerkProvider component
  logger.log('Clerk configuration complete');
};

export default configureClerk;
